/**
 * App-wide constants. Centralized so paths/keys are never stringly
 * duplicated across the codebase.
 */

/** Canonical route paths. Use these for navigation and redirects. */
export const ROUTE_PATHS = {
  root: '/',
  login: '/login',
  admin: '/admin',
  unauthorized: '/unauthorized',
  /** Public, no-login client wrapper opened from the SMS invite link. */
  clientVerify: '/verify',
  /** Public Terms & Conditions screen shown to invited users before login. */
  inviteTerms: '/invite-terms',
  /** Public MFA-reset page opened from the admin-initiated reset email. */
  resetMfa: '/reset-mfa',
  // Primary navigation sections (Figma "Verifications" layout).
  accounts: '/accounts',
  /**
   * Case detail. Deep-linkable, so it does not depend on a cases list existing —
   * `$caseId` is filled in by the router. The list route (`/cases`) is a later
   * ticket; add it here when it ships.
   */
  caseView: '/cases/$caseId/view',
  verifications: '/verifications',
  /** New Cases area (AMLIQ-253) — built new, runs alongside Verifications. */
  cases: '/cases',
  learnCentre: '/learn-centre',
  userManagement: '/user-management',
  accountSettings: '/account-settings',
  support: '/support',
} as const

export type RoutePath = (typeof ROUTE_PATHS)[keyof typeof ROUTE_PATHS]

/** localStorage keys (namespaced to avoid collisions). */
export const STORAGE_KEYS = {
  auth: 'amliq.auth',
  ui: 'amliq.ui',
  /** Super-admin impersonation session — persisted to sessionStorage (per-tab). */
  impersonation: 'amliq.impersonation',
} as const

/**
 * Email + authenticator code is mandatory (AMLIQ-264): admins can't turn it off,
 * so the client treats `mfaLoginEnabled` as `true` no matter what the API sends,
 * and always writes `true` back. The API still owns the field and older tenants
 * may hold a stale `false`, so this normalizes on both read and write —
 * see `mapTenant` in the two `*.api.ts` files and `EmailLoginForm`.
 *
 * To make it configurable again, flip this to `false` and restore the Login
 * methods switch — the read/write normalization then falls back on its own.
 *
 * `as boolean` widens the literal type on purpose: without it TS narrows this
 * to `true`, and `no-unnecessary-condition` rejects every `MFA_LOGIN_IS_MANDATORY ? … : …`
 * as provably dead. Widening keeps those branches valid so flipping the flag is
 * a one-line change.
 */
export const MFA_LOGIN_IS_MANDATORY = true as boolean

/** Default pagination used across list views. */
export const PAGINATION = {
  defaultPage: 1,
  defaultPageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
} as const

/** TanStack Query default cache timings (ms). */
export const QUERY_DEFAULTS = {
  staleTime: 60_000, // 1 min
  gcTime: 5 * 60_000, // 5 min
  retry: 1,
} as const

/**
 * Background auto-refresh cadence for the views that opt into it (ms), so users
 * don't have to reload the page to see new data.
 *
 * This is NOT a default — a query only polls if it asks to. Each polling view
 * costs one request per tick per open tab, so treat this as a shared budget:
 * lowering it multiplies load across every user with the page open. Polling
 * pauses automatically in hidden tabs (React Query's `refetchIntervalInBackground`
 * default).
 */
export const AUTO_REFRESH_INTERVAL = 15_000
